import { EligibilityOffer } from '../../components/ProductCard/types';
import {
  CmsGift,
  CmsOffer,
  CmsProduct,
  PeriodGiftListItem,
} from '../../types/cmsProduct';
import { PeriodGift } from '../../types/cmsTypes';
import { filterGiftsByAmountAndTenure } from './attrHelpers';
import { PersonalLoanFilterConfig } from './types';

/**
 * The offers the user picks one gift from: exclusive + bundle. Welcome offers are the provider's
 * own gift and form a group of their own (CMS-2032).
 */
export const getChooseOneOffers = (
  product: Pick<CmsProduct, 'exclusive_offers' | 'bundle_exclusive_offers'>,
): CmsOffer[] => [
  ...(product.exclusive_offers ?? []),
  ...(product.bundle_exclusive_offers ?? []),
];

export function mapOfferToPeriodGifts(
  offers?: EligibilityOffer[],
  mapIneligibleGifts: boolean = false,
): PeriodGift[] | undefined {
  if (!offers) return undefined;

  return offers
    .flatMap((o) => {
      const periodListGifts: PeriodGiftListItem[] =
        o?.[mapIneligibleGifts ? 'ineligible_list' : 'list_of_gifts']?.map(
          (l) => ({
            ...l,
            startDate: o.period?.start_date,
            endDate: o.period?.end_date,
            offerTitle: o.offer_title,
          }),
        ) ?? [];

      return periodListGifts;
    })
    .map((lg): PeriodGift => {
      const tenure = lg.criteria_list?.find(
        (c) => c.name?.name === 'ELIGIBLE_TENURE_RANGE',
      );
      const amount = lg.criteria_list?.find(
        (c) => c.name?.name === 'ELIGIBLE_LOAN_RANGE',
      );

      return {
        ...(lg.gift as CmsGift),
        startDate: lg.startDate!,
        endDate: lg.endDate!,
        minTenure: parseNumberOrUndefined(tenure?.value_start),
        maxTenure: parseNumberOrUndefined(tenure?.value_end),
        minAmount: parseNumberOrUndefined(amount?.value_start),
        maxAmount: parseNumberOrUndefined(amount?.value_end),
        offerTitle: lg.offerTitle,
      };
    })
    .filter((g): g is PeriodGift => !!g);
}

/** The offer that ends first; the first offer when none states an end date. */
export const getEarliestEndingOffer = (
  offers: CmsOffer[],
): CmsOffer | undefined => {
  const withEndDate = offers.filter(
    (x): x is CmsOffer & { period: { end_date: string } } =>
      !!x.period?.end_date,
  );
  const sorted = withEndDate.sort(
    (a, b) =>
      new Date(a.period.end_date).getTime() -
      new Date(b.period.end_date).getTime(),
  );
  return sorted[0] ?? offers[0];
};

export const filterActiveOffers = (offers: CmsOffer[], now: Date): CmsOffer[] => {
  const nowMs = now.getTime();

  return offers.filter((offer) => {
    const start = offer.period?.start_date;
    const end = offer.period?.end_date;

    if (start && nowMs < new Date(start).getTime()) return false;
    if (end && nowMs > new Date(end).getTime()) return false;

    return true;
  });
};

const parseNumberOrUndefined = (value?: string) => {
  const parsed = value ? parseInt(value, 10) : undefined;
  if (parsed === undefined) return undefined;
  return isNaN(parsed) ? undefined : parsed;
};

export const filterOffersByAmountAndTenure = (
  offers: CmsOffer[],
  amount?: string,
  tenure?: string,
  personalLoanfilterConfig?: PersonalLoanFilterConfig,
): EligibilityOffer[] => {
  if (!amount && !tenure) return offers;

  const amountNumber = amount ? parseInt(amount) : undefined;
  const tenureNumber = tenure ? parseInt(tenure) : undefined;

  return offers.map((offer) => {
    const separated = filterGiftsByAmountAndTenure(
      offer.list_of_gifts,
      amountNumber,
      tenureNumber,
      personalLoanfilterConfig,
    );

    return {
      ...offer,
      list_of_gifts: separated.matchingAmountTenure,
      ineligible_list: separated.nonMatchingAmountTenure,
    };
  });
};
